import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Bell,
  BarChart3,
  BookOpen,
  CalendarDays,
  ChevronDown,
  ClipboardCheck,
  ClipboardList,
  FileArchive,
  FileSpreadsheet,
  GraduationCap,
  HeartHandshake,
  LayoutDashboard,
  LogOut,
  School,
  Settings2,
  Sheet,
  UserRoundCog,
  UsersRound,
  X,
} from "lucide-react";
import { NavLink, Outlet, useLocation } from "../lib/router";
import { applyPersonalTheme } from "../lib/apply-personal-theme";
import { type PersonalTheme } from "../lib/personal-theme";

export type WorkspaceResponse = {
  user: { id: string; email: string; displayName: string; accountType: "super_admin" | "staff" | "student" };
  roles: string[];
  permissions: string[];
  scopes: Array<{ type: string; id: string }>;
  menu: Array<{ key: string; label: string; href: string }>;
  activeAcademicContext: {
    academicYearId: string;
    yearBuddhist: number;
    semester: { id: string; number: number; startsOn: string | null; endsOn: string | null } | null;
  } | null;
  sessionExpiresAt: string;
  theme: PersonalTheme;
  unreadNotificationCount?: number;
};

export type WorkspaceOutletContext = WorkspaceResponse & {
  shell: {
    sidebarCollapsed: boolean;
    toggleSidebar: () => void;
  };
};

export async function loadWorkspace(): Promise<WorkspaceResponse | null> {
  const response = await fetch("/api/v1/me/workspace", {
    credentials: "same-origin",
    headers: { Accept: "application/json" },
  });
  if (response.status === 401) return null;
  if (!response.ok) throw new Error("ไม่สามารถโหลดพื้นที่ทำงานได้");
  return (await response.json()) as WorkspaceResponse;
}

const workspaceCacheKey = "kps-workspace-cache:v1";
const workspaceCacheMaxAgeMs = 5 * 60_000;

function readWorkspaceCache() {
  try {
    const raw = sessionStorage.getItem(workspaceCacheKey);
    const cached = raw ? JSON.parse(raw) as { savedAt: number; data: WorkspaceResponse } : null;
    if (!cached || !Number.isFinite(cached.savedAt) || Date.now() - cached.savedAt > workspaceCacheMaxAgeMs) return null;
    return cached;
  } catch {
    sessionStorage.removeItem(workspaceCacheKey);
    return null;
  }
}

type NavItem = {
  label: string;
  to: string;
  icon: typeof LayoutDashboard;
  end?: boolean;
  badge?: number;
};

const roleLabels: Record<string, string> = {
  super_admin: "ผู้ดูแลสูงสุด",
  school_admin: "ผู้ดูแลระบบ",
  academic_admin: "งานวิชาการ",
  academic_staff: "ฝ่ายวิชาการ",
  substitution_manager: "จัดสอนแทน",
  year_rollover_reviewer: "ผู้ตรวจการเลื่อนชั้น",
  year_rollover_approver: "ผู้อนุมัติการเลื่อนชั้น",
  teacher: "ครูผู้สอน",
  student: "นักเรียน",
};

function navigationGroupForPath(path: string) {
  if (path.startsWith("/student/")) return "self";
  if (path.startsWith("/admin/master-data/student-") || path.startsWith("/admin/master-data/students/")
    || path.startsWith("/admin/master-data/year-rollover")) return "students";
  if (path.startsWith("/attendance") || path.startsWith("/activity-attendance")) return "teaching";
  if (path.startsWith("/scores") || path.startsWith("/admin/settings/pp5")) return "teaching";
  if (path.startsWith("/exams/")) return "exams";
  if (path.startsWith("/behavior")) return "care";
  if (path.startsWith("/admin/master-data") || path.startsWith("/admin/substitutions")) return "school";
  return "primary";
}

export default function AppShell() {
  const location = useLocation();
  const queryClient = useQueryClient();
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const sidebarRef = useRef<HTMLElement>(null);
  const navigationRef = useRef<HTMLDivElement>(null);
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopSidebarCollapsed, setDesktopSidebarCollapsed] = useState(false);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => ({
    [navigationGroupForPath(location.pathname)]: true,
  }));
  const [loggingOut, setLoggingOut] = useState(false);
  const cachedWorkspace = useMemo(readWorkspaceCache, []);
  const workspace = useQuery({
    queryKey: ["workspace"], queryFn: loadWorkspace, staleTime: workspaceCacheMaxAgeMs,
    initialData: cachedWorkspace?.data, initialDataUpdatedAt: cachedWorkspace?.savedAt,
  });
  useEffect(() => {
    if (workspace.data) sessionStorage.setItem(workspaceCacheKey, JSON.stringify({ savedAt: Date.now(), data: workspace.data }));
  }, [workspace.data]);
  useEffect(() => { applyPersonalTheme(workspace.data?.theme); }, [workspace.data?.theme]);
  const closeMobileDrawer = () => {
    setMobileOpen(false);
    requestAnimationFrame(() => menuButtonRef.current?.focus());
  };

  useEffect(() => {
    setMobileOpen(false);
    setDesktopSidebarCollapsed(false);
    const activeGroup = navigationGroupForPath(location.pathname);
    setOpenGroups((current) => ({ ...current, [activeGroup]: true }));
  }, [location.pathname]);

  const toggleWorkspaceSidebar = () => {
    if (window.matchMedia("(max-width: 960px)").matches) {
      setMobileOpen(true);
      return;
    }
    setDesktopSidebarCollapsed((current) => !current);
  };
  useLayoutEffect(() => {
    const navigation = navigationRef.current;
    if (!navigation) return;
    const saved = Number(sessionStorage.getItem("kps-sidebar-scroll") ?? 0);
    if (Number.isFinite(saved)) navigation.scrollTop = saved;
  }, [location.pathname, openGroups, workspace.data]);
  useEffect(() => {
    const onTouchStart = (event: TouchEvent) => {
      const touch = event.touches[0];
      if (!touch || window.innerWidth > 960) return;
      if (mobileOpen ? touch.clientX <= (sidebarRef.current?.getBoundingClientRect().right ?? 0) : touch.clientX <= 28) {
        touchStartRef.current = { x: touch.clientX, y: touch.clientY };
      }
    };
    const onTouchEnd = (event: TouchEvent) => {
      const start = touchStartRef.current;
      touchStartRef.current = null;
      const touch = event.changedTouches[0];
      if (!start || !touch || Math.abs(touch.clientY - start.y) > 55) return;
      const distance = touch.clientX - start.x;
      if (!mobileOpen && distance > 65) setMobileOpen(true);
      if (mobileOpen && distance < -65) closeMobileDrawer();
    };
    document.addEventListener("touchstart", onTouchStart, { passive: true });
    document.addEventListener("touchend", onTouchEnd, { passive: true });
    return () => {
      document.removeEventListener("touchstart", onTouchStart);
      document.removeEventListener("touchend", onTouchEnd);
    };
  }, [mobileOpen]);
  useEffect(() => {
    const desktop = window.matchMedia("(min-width: 961px)");
    const closeDrawer = () => { if (window.innerWidth > 960) setMobileOpen(false); };
    desktop.addEventListener("change", closeDrawer);
    window.addEventListener("resize", closeDrawer);
    window.visualViewport?.addEventListener("resize", closeDrawer);
    closeDrawer();
    return () => {
      desktop.removeEventListener("change", closeDrawer);
      window.removeEventListener("resize", closeDrawer);
      window.visualViewport?.removeEventListener("resize", closeDrawer);
    };
  }, []);
  useEffect(() => {
    if (!mobileOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    sidebarRef.current?.focus();
    const handleDrawerKeyboard = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeMobileDrawer();
        return;
      }
      if (event.key === "Tab" && sidebarRef.current) {
        const focusable = Array.from(sidebarRef.current.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
        )).filter((element) => element.getClientRects().length > 0);
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && (document.activeElement === first || document.activeElement === sidebarRef.current)) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }
    };
    document.addEventListener("keydown", handleDrawerKeyboard);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleDrawerKeyboard);
    };
  }, [mobileOpen]);

  const availableKeys = useMemo(() => new Set(workspace.data?.menu.map((item) => item.key) ?? []), [workspace.data]);
  const primary: NavItem[] = [
    ...(availableKeys.has("home") ? [{ label: "ตารางประจำสัปดาห์", to: "/workspace", icon: LayoutDashboard, end: true }] : []),
    ...(availableKeys.has("advisory-leaves") ? [{ label: "ลานักเรียน", to: "/advisory/leaves", icon: CalendarDays, end: true }] : []),
    ...(availableKeys.has("substitutions") ? [{ label: "คาบสอนแทน", to: "/substitutions", icon: BookOpen, end: true }] : []),
    ...(availableKeys.has("notifications") ? [{ label: "การแจ้งเตือน", to: "/notifications", icon: Bell, end: true, badge: workspace.data?.unreadNotificationCount ?? 0 }] : []),
  ];
  const self: NavItem[] = [
    ...(availableKeys.has("student-home") ? [{ label: "หน้าแรก", to: "/student/home", icon: LayoutDashboard, end: true }] : []),
    ...(availableKeys.has("student-exams") ? [{ label: "ห้องสอบของฉัน", to: "/student/exams", icon: ClipboardList, end: true }] : []),
    ...(availableKeys.has("student-assignments") ? [{ label: "งานของฉัน", to: "/student/assignments", icon: FileArchive, end: true }] : []),
    ...(availableKeys.has("student-results") ? [{ label: "ผลการเรียนของฉัน", to: "/student/results", icon: GraduationCap, end: true }] : []),
  ];
  const attendance: NavItem[] = availableKeys.has("attendance")
    ? [{ label: "เช็กชื่อนักเรียน", to: "/attendance", icon: ClipboardCheck, end: true }]
    : availableKeys.has("activity-attendance")
      ? [{ label: "เช็กชื่อ", to: "/activity-attendance", icon: ClipboardCheck, end: true }]
      : [];
  const scores: NavItem[] = [
    ...(availableKeys.has("scores-entry") ? [{ label: "บันทึกคะแนน", to: "/scores/entry", icon: Sheet, end: true }] : []),
    ...(availableKeys.has("scores-reports") ? [{ label: "รายงานผลการเรียน", to: "/scores/reports", icon: FileSpreadsheet, end: true }] : []),
  ];
  if (!scores.length) {
    if (availableKeys.has("scores-review")) scores.push({ label: "ตรวจตารางคะแนน", to: "/scores/review", icon: ClipboardCheck, end: true });
    if (availableKeys.has("scores-corrections")) scores.push({ label: "คิวแก้ผลการเรียน", to: "/scores/corrections", icon: ClipboardCheck, end: true });
    if (availableKeys.has("scores-readiness")) scores.push({ label: "ความพร้อมประกาศผล", to: "/scores/readiness", icon: ClipboardCheck, end: true });
    if (availableKeys.has("master-data")) scores.push({ label: "ตั้งค่า ปพ.5", to: "/admin/settings/pp5", icon: FileSpreadsheet, end: true });
  }
  const exams: NavItem[] = availableKeys.has("exam-bank")
    ? [
      { label: "เพิ่มข้อสอบ", to: "/exams/bank?step=questions", icon: ClipboardList, end: true },
    ]
    : [];
  if (availableKeys.has("exam-proctor")) exams.push({ label: "คุมสอบสด", to: "/exams/proctor", icon: ClipboardCheck, end: true });
  if (availableKeys.has("exam-essay-grading")) exams.push({ label: "ตรวจข้อสอบอัตนัย", to: "/exams/essay-grading", icon: ClipboardCheck, end: true });
  if (availableKeys.has("exam-analysis")) exams.push({ label: "วิเคราะห์ข้อสอบ", to: "/exams/analysis", icon: BarChart3, end: true });
  const behavior: NavItem[] = [
    ...(availableKeys.has("behavior-record") ? [{ label: "บันทึกพฤติกรรม", to: "/behavior/record", icon: HeartHandshake, end: true }] : []),
    ...(availableKeys.has("behavior-summary") ? [{ label: "สรุปคะแนนพฤติกรรม", to: "/behavior/summary", icon: BarChart3, end: true }] : []),
  ];
  if (!behavior.length && availableKeys.has("behavior-categories")) behavior.push({ label: "ตั้งค่าหัวข้อพฤติกรรม", to: "/behavior/categories", icon: Settings2, end: true });
  const studentEntry: NavItem | null = availableKeys.has("master-data")
    ? { label: "รายชื่อนักเรียน", to: "/admin/master-data/student-roster", icon: UsersRound, end: true }
    : availableKeys.has("student-accounts")
      ? { label: "บัญชีนักเรียน", to: "/admin/master-data/student-accounts", icon: UsersRound, end: true }
      : availableKeys.has("year-rollover")
        ? { label: "เลื่อนชั้นปีถัดไป", to: "/admin/master-data/year-rollover", icon: UsersRound, end: true }
        : null;
  const students: NavItem[] = studentEntry ? [studentEntry] : [];
  const school: NavItem[] = availableKeys.has("master-data") ? [
    { label: "ปีและห้องเรียน", to: "/admin/master-data/academic-context", icon: School, end: true },
    { label: "ข้อมูลครู", to: "/admin/master-data/teachers", icon: UserRoundCog, end: true },
    { label: "จัดการการสอน", to: "/admin/master-data/courses", icon: GraduationCap },
  ] : [];
  if (availableKeys.has("activity-attendance-settings")) school.push({ label: "ตั้งค่ากิจกรรม", to: "/admin/master-data/activity-attendance", icon: Settings2, end: true });
  if (availableKeys.has("admin-substitutions")) school.push({ label: "จัดครูสอนแทน", to: "/admin/substitutions", icon: UserRoundCog, end: true });
  if (availableKeys.has("assignment-files")) school.push({ label: "จัดการไฟล์งานเก่า", to: "/admin/master-data/assignment-files", icon: FileArchive, end: true });

  async function logout() {
    setLoggingOut(true);
    try {
      await fetch("/api/v1/auth/logout", { method: "POST", credentials: "same-origin" });
      sessionStorage.removeItem(workspaceCacheKey);
      queryClient.setQueryData(["workspace"], null);
      applyPersonalTheme(null);
      window.location.assign("/");
    } finally {
      setLoggingOut(false);
    }
  }

  if (workspace.isPending) {
    return <main className="route-state" aria-busy="true"><span className="spinner" aria-hidden="true" /><p>กำลังเตรียมพื้นที่ทำงาน…</p></main>;
  }

  if (!workspace.data) {
    return <main className="shell-auth-state"><img src="/school-emblem.png" alt="" /><h1>กรุณาเข้าสู่ระบบ</h1><p>เซสชันของคุณหมดอายุหรือยังไม่ได้เข้าสู่ระบบ</p><a href="/">กลับหน้าเข้าสู่ระบบ</a></main>;
  }

  const studentWorkspaceActive = navigationGroupForPath(location.pathname) === "students";
  const renderGroup = (key: string, label: string, GroupIcon: typeof LayoutDashboard, items: NavItem[]) => items.length > 0 && (
    <section className={`sidebar-group ${openGroups[key] ? "is-open" : ""}`} aria-label={label}>
      <button
        className="sidebar-group-toggle"
        type="button"
        aria-expanded={Boolean(openGroups[key])}
        aria-controls={`sidebar-group-${key}`}
        title={label}
        aria-label={label}
        onClick={() => setOpenGroups((current) => ({ ...current, [key]: !current[key] }))}
      >
        <GroupIcon aria-hidden="true" />
        <span>{label}</span>
        <ChevronDown className="sidebar-group-chevron" aria-hidden="true" />
      </button>
      <nav id={`sidebar-group-${key}`} hidden={!openGroups[key]}>{items.map(({ label: itemLabel, to, icon: Icon, end, badge }) => (
        <NavLink key={to} to={to} end={end} title={itemLabel} aria-label={itemLabel}
          className={key === "students" && studentWorkspaceActive && location.pathname !== to ? "active" : undefined}
          aria-current={key === "students" && studentWorkspaceActive ? "location" : undefined}>
          <Icon aria-hidden="true" strokeWidth={1.9} /><span>{itemLabel}</span>
          {Boolean(badge) && <b className="sidebar-badge">{badge && badge > 99 ? "99+" : badge}</b>}
        </NavLink>
      ))}</nav>
    </section>
  );

  const activeContext = workspace.data.activeAcademicContext;
  const activeContextValue = activeContext
    ? `${activeContext.yearBuddhist}${activeContext.semester ? ` · ภาคเรียน ${activeContext.semester.number}` : " · ยังไม่เปิดภาคเรียน"}`
    : "ยังไม่ได้เปิดปีการศึกษา";
  const activeContextTitle = `ปีที่ระบบใช้งาน: ${activeContextValue}`;
  const activeContextContent = <>
    <span className={`sidebar-context-status ${activeContext?.semester ? "is-open" : "is-pending"}`} aria-hidden="true" />
    <span className="sidebar-context-copy"><small>ปีที่ระบบใช้งาน</small><strong>{activeContextValue}</strong></span>
  </>;

  return (
    <div className={`app-shell ${mobileOpen ? "is-mobile-open" : ""} ${desktopSidebarCollapsed ? "is-sidebar-collapsed" : ""}`}>
      <button ref={menuButtonRef} className="mobile-edge-opener" type="button" onClick={() => setMobileOpen(true)} aria-label="แตะขอบซ้ายเพื่อเปิดเมนู" />
      <button className="sidebar-scrim" type="button" onClick={closeMobileDrawer} aria-label="ปิดเมนู" />
      <aside className="app-sidebar" ref={sidebarRef} tabIndex={-1}>
        <div className="sidebar-brand">
          <img src="/school-emblem.png" alt="ตราโรงเรียนคำสร้อยพิทยาสรรค์" />
          <span><strong>KPS ACADEMIC</strong><small>ระบบงานวิชาการ</small></span>
          <button className="sidebar-mobile-close" type="button" onClick={closeMobileDrawer} aria-label="ปิดเมนู"><X /></button>
        </div>

        {availableKeys.has("master-data") ? (
          <NavLink className="sidebar-academic-context is-link" to="/admin/master-data/academic-context" title={activeContextTitle} aria-label={`${activeContextTitle} ไปหน้าปีและห้องเรียน`}>
            {activeContextContent}
          </NavLink>
        ) : (
          <div className="sidebar-academic-context" title={activeContextTitle} aria-label={activeContextTitle}>
            {activeContextContent}
          </div>
        )}

        <div className="sidebar-navigation" ref={navigationRef} onScroll={(event) => sessionStorage.setItem("kps-sidebar-scroll", String(event.currentTarget.scrollTop))}>
          {renderGroup("primary", "งานของฉัน", LayoutDashboard, primary)}
          {renderGroup("self", "ข้อมูลของฉัน", GraduationCap, self)}
          {renderGroup("teaching", "การสอนและวัดผล", BookOpen, [...attendance, ...scores])}
          {renderGroup("exams", "ศูนย์การวัดผล", ClipboardList, exams)}
          {renderGroup("care", "ดูแลนักเรียน", HeartHandshake, behavior)}
          {renderGroup("students", "จัดการนักเรียน", UsersRound, students)}
          {renderGroup("school", "จัดการโรงเรียน", Settings2, school)}
          <nav className="sidebar-personal" aria-label="ตั้งค่าส่วนตัว">
            <NavLink to="/settings/personal" end title="ตั้งค่าส่วนตัว"><Settings2 aria-hidden="true" strokeWidth={1.9} /><span>ตั้งค่าส่วนตัว</span></NavLink>
          </nav>
        </div>

        <div className="sidebar-footer">
          <div className="sidebar-account" title={workspace.data.user.displayName}>
            <span>{workspace.data.user.displayName.trim().charAt(0) || "K"}</span>
            <div><strong>{workspace.data.user.displayName}</strong><small>{workspace.data.roles.map((role) => roleLabels[role] ?? role).join(" · ")}</small></div>
          </div>
          <div className="sidebar-footer-actions">
            <button type="button" onClick={() => void logout()} disabled={loggingOut} title="ออกจากระบบ"><LogOut /><span>{loggingOut ? "กำลังออก…" : "ออกจากระบบ"}</span></button>
          </div>
        </div>
      </aside>
      <div className="app-shell-content">
        <Outlet context={{ ...workspace.data, shell: { sidebarCollapsed: desktopSidebarCollapsed, toggleSidebar: toggleWorkspaceSidebar } } satisfies WorkspaceOutletContext} />
      </div>
    </div>
  );
}
